<template>
  <form class="cat-form" :class="{ 'cat-form--page': layout === 'page' }" dir="rtl" @submit.prevent="onSubmit" novalidate>
    <!-- main info -->
    <section class="cat-card cat-area-main">
      <div class="cat-card__head">
        <h3 class="cat-card__title">
          <span class="cat-card__icon"><b-icon icon="card-heading"/></span>
          اطلاعات دسته
        </h3>
        <span class="cat-switch-row" :class="{ 'is-on': form.status }">
          <span class="cat-switch-row__text">{{ form.status ? 'نمایش فعال' : 'مخفی' }}</span>
          <button type="button" role="switch" class="cat-switch" :class="{ 'is-on': form.status }"
                  :aria-checked="form.status ? 'true' : 'false'" @click="form.status = !form.status">
            <span class="cat-switch__knob"></span>
          </button>
        </span>
      </div>
      <div class="cat-card__body cat-stack">
        <div class="cat-grid">
          <form-group :validations="$v.form.name">
            <div slot-scope="{ errors, invalid }">
              <label class="cat-label">نام دسته</label>
              <input v-model="form.name" type="text" class="form-control cat-input" :class="{ invalid }"
                     placeholder="نام دسته" @input="onNameInput">
              <form-errors-manager :errors="errors"/>
            </div>
          </form-group>
          <form-group :validations="$v.form.slug">
            <div slot-scope="{ errors, invalid }">
              <label class="cat-label">slug</label>
              <input v-model="form.slug" type="text" dir="ltr" class="form-control cat-input" :class="{ invalid }"
                     placeholder="category-slug" @input="onSlugInput">
              <form-errors-manager :errors="errors"/>
            </div>
          </form-group>
          <form-group :validations="$v.form.order">
            <div slot-scope="{ errors, invalid }">
              <label class="cat-label">ترتیب</label>
              <input v-model="form.order" type="number" min="1" class="form-control cat-input" :class="{ invalid }"
                     placeholder="1">
              <form-errors-manager :errors="errors"/>
            </div>
          </form-group>
        </div>

        <!-- parent picker -->
        <div class="cat-parent">
          <label class="cat-label">دسته والد</label>
          <div class="cat-parent__current">
            <span v-if="selectedParentNode" class="cat-chip">
              <span class="cat-chip__path" v-if="selectedParentPath">{{ selectedParentPath }} ›</span>
              {{ selectedParentNode.data.name }}
              <button type="button" class="cat-chip__x" @click="clearParent" aria-label="حذف والد"><b-icon icon="x"/></button>
            </span>
            <span v-else class="cat-muted">بدون والد (دسته اصلی)</span>
          </div>
          <div class="cat-search">
            <b-icon icon="search" class="cat-search__icon"/>
            <input v-model="parentSearch" class="cat-search__input" placeholder="جستجوی دسته والد..."
                   @keydown.enter.prevent>
          </div>
          <div class="cat-picker">
            <button type="button" v-for="row in parentRows" :key="'p-' + row.node.key" class="cat-picker__item"
                    :class="{ 'is-active': isSelectedParent(row.node), 'is-match': row.isMatch }"
                    :style="{ paddingRight: (0.6 + row.node.depth * 1.1) + 'rem' }"
                    @click="selectParent(row.node)">
              <b-icon :icon="row.node.children.length ? 'folder2' : 'dot'" class="cat-picker__icon"/>
              <span class="cat-picker__name">{{ row.node.data.name }}</span>
              <b-icon v-if="isSelectedParent(row.node)" icon="check2" class="cat-picker__check"/>
            </button>
            <p v-if="!parentRows.length" class="cat-picker__empty">دسته‌ای پیدا نشد.</p>
          </div>
        </div>

        <form-group :validations="$v.form.description">
          <div slot-scope="{ errors }">
            <label class="cat-label">توضیحات</label>
            <textarea v-model="form.description" class="form-control cat-input" rows="4"
                      placeholder="توضیحات (اختیاری)"></textarea>
            <form-errors-manager :errors="errors"/>
          </div>
        </form-group>
      </div>
    </section>

    <aside class="cat-side">
      <!-- image (first on mobile) -->
      <section class="cat-card cat-area-image">
        <div class="cat-card__head">
          <h3 class="cat-card__title">
            <span class="cat-card__icon"><b-icon icon="image"/></span>
            تصویر دسته
          </h3>
          <button v-if="form.preview && isEdit" type="button" class="cat-link-btn is-danger" @click="removeImage">
            <b-icon icon="trash"/> حذف
          </button>
        </div>
        <div class="cat-card__body">
          <input :id="imageInputId" type="file" accept="image/*" class="d-none" @change="onFileChange">
          <label :for="imageInputId" class="cat-image-picker" :class="{ 'is-empty': !form.preview }">
            <img v-if="form.preview" :src="form.preview" alt="">
            <template v-else>
              <b-icon icon="cloud-arrow-up" class="cat-image-picker__icon"/>
              <span>انتخاب تصویر (اختیاری)</span>
            </template>
            <span v-if="form.preview" class="cat-image-picker__change"><b-icon icon="camera"/> تغییر</span>
          </label>
        </div>
      </section>

    <!-- language -->
    <section class="cat-card cat-area-lang">
      <div class="cat-card__head">
        <h3 class="cat-card__title">
          <span class="cat-card__icon"><b-icon icon="translate"/></span>
          زبان
        </h3>
        <span class="cat-status-pill" :class="{ 'is-set': !!currentLanguage }">
          <span class="cat-status-pill__dot"></span>
          {{ currentLanguage ? currentLanguage.name : 'انتخاب نشده' }}
        </span>
      </div>
      <div class="cat-card__body">
        <form-group :validations="$v.form.language_id">
          <div slot-scope="{ errors }">
            <div class="cat-tabs" role="radiogroup">
              <button v-for="item in languages" :key="item.id" type="button" class="cat-tab"
                      :class="{ 'is-active': String(form.language_id) === String(item.id) }"
                      @click="form.language_id = item.id">
                <b-icon v-if="String(form.language_id) === String(item.id)" icon="check2"/>
                {{ item.name }}
              </button>
            </div>
            <form-errors-manager :errors="errors"/>
          </div>
        </form-group>
        <template v-if="isEdit && otherLanguages.length">
          <p class="cat-hint">ترجمه این دسته در زبان دیگر:</p>
          <div class="cat-translations">
            <button type="button" v-for="item in otherLanguages" :key="'tr-' + item.id" class="cat-add-translation"
                    @click="$emit('add-translation', { language_id: item.id, name: form.name, description: form.description, parent_id: form.parent_id })">
              <b-icon icon="plus-circle"/> افزودن نسخه {{ item.name }}
            </button>
          </div>
        </template>
      </div>
    </section>

    <div class="cat-area-actions cat-actions">
      <button type="button" v-if="showCancel" class="cat-btn cat-btn--ghost" @click="$emit('cancel')">انصراف</button>
      <button type="submit" class="cat-btn cat-btn--primary" :disabled="saving">
        <b-spinner v-if="saving" small/>
        <b-icon v-else icon="check2-circle"/>
        {{ isEdit ? 'ذخیره تغییرات' : 'ثبت دسته' }}
      </button>
    </div>
    </aside>
  </form>
</template>

<script>
import {mapGetters} from "vuex";
import {minValue, numeric, required} from "vuelidate/lib/validators";
import {slug} from "@moeinmdn/mixitweb-front-modules/src/plugin/class/VuelidateCustomValidatoins";
import {toast} from "@moeinmdn/mixitweb-front-modules/src/plugin/class/Notification";
import FormGroup from "../../validator/FormGroup";
import FormErrorsManager from "../../validator/FormErrorsManager.vue";
import {
  buildCategoryTree,
  categoryKey,
  categoryPathNames,
  collectDescendantIds,
  filterCategoryTree,
  visibleCategoryRows
} from "./categoryTree";

let formSeq = 0

export default {
  name: "CategoryForm",
  components: {FormGroup, FormErrorsManager},
  props: {
    category: {type: Object, default: null},     // null => create
    records: {type: Array, default: () => []},    // flat category records (for the parent picker)
    tree: {type: Object, default: null},          // optional prebuilt tree (memoized by the parent)
    defaults: {type: Object, default: () => ({})},
    layout: {type: String, default: 'modal'},     // modal | page
    showCancel: {type: Boolean, default: false},
  },
  data() {
    formSeq++
    return {
      imageInputId: `cat-image-${formSeq}`,
      saving: false,
      slugAuto: true,
      parentSearch: '',
      form: this.emptyForm(),
    }
  },
  computed: {
    ...mapGetters({
      languages: "Language/getLanguages",
    }),
    isEdit() {
      return !!(this.category && this.category.id)
    },
    pickerTree() {
      return this.tree || buildCategoryTree(this.records)
    },
    excludedKeys() {
      return this.isEdit ? collectDescendantIds(this.pickerTree, this.category.id) : null
    },
    parentRows() {
      const filter = filterCategoryTree(this.pickerTree, this.parentSearch)
      return visibleCategoryRows(this.pickerTree, {filter, exclude: this.excludedKeys})
    },
    selectedParentNode() {
      const key = categoryKey(this.form.parent_id)
      return key === null ? null : (this.pickerTree.byId.get(key) || null)
    },
    selectedParentPath() {
      return this.selectedParentNode ? categoryPathNames(this.selectedParentNode).join(' › ') : ''
    },
    currentLanguage() {
      return (this.languages || []).find(item => String(item.id) === String(this.form.language_id)) || null
    },
    otherLanguages() {
      return (this.languages || []).filter(item => String(item.id) !== String(this.form.language_id))
    },
  },
  validations: {
    form: {
      name: {required},
      description: {},
      image: {},
      slug: {required, slug},
      language_id: {required},
      order: {required, numeric, minValue: minValue(1)},
      parent_id: {},
    },
  },
  watch: {
    category() {
      this.fillForm()
    },
    defaults() {
      if (!this.isEdit) this.fillForm()
    }
  },
  methods: {
    emptyForm() {
      return {
        id: '',
        name: '',
        description: '',
        image: [],
        parent_id: null,
        parent: {},
        status: true,
        order: 1,
        language_id: '',
        slug: '',
        preview: ''
      }
    },
    defaultLanguageId() {
      const lang = this.findAttributeOnSiteInfo ? this.findAttributeOnSiteInfo('default_language') : null
      return lang && lang[0] ? lang[0].id : ''
    },
    fillForm() {
      const form = this.emptyForm()
      if (this.isEdit) {
        const c = this.category
        Object.assign(form, {
          id: c.id,
          name: c.name || '',
          description: c.description || '',
          preview: c.image || '',
          parent_id: categoryKey(c.parent_id) === null ? null : c.parent_id,
          parent: c.parent || {},
          slug: c.slug || '',
          order: c.order || 1,
          language_id: c.language_id,
          status: c.status === 1 || c.status === true || c.status === '1',
        })
        this.slugAuto = !form.slug
      } else {
        const d = this.defaults || {}
        form.language_id = d.language_id || this.defaultLanguageId()
        form.parent_id = categoryKey(d.parent_id) === null ? null : d.parent_id
        if (d.name) form.name = d.name
        if (d.description) form.description = d.description
        form.slug = this.makeSlug(form.name)
        this.slugAuto = true
      }
      this.form = form
      this.parentSearch = ''
      this.$v.$reset()
    },
    // same slugify as the product quick-create forms
    makeSlug(name) {
      if (!name) return ''
      let link = String(name).trim().toLowerCase()
      link = link.replace(/ +/g, "-")
      link = link.replace(/[^\wآابپتثجچحخدذرزژسشصضطظعغفقکگلمنوهی]+/g, "-")
      return link.replace(/-+/g, "-").replace(/^-|-$/g, "")
    },
    onNameInput() {
      if (this.slugAuto) this.form.slug = this.makeSlug(this.form.name)
    },
    onSlugInput() {
      this.slugAuto = !this.form.slug
    },
    onFileChange(e) {
      const file = e.target.files && e.target.files[0]
      if (!file) return
      this.form.image = file
      this.form.preview = URL.createObjectURL(file)
      e.target.value = ''
    },
    removeImage() {
      if (!confirm('تصویر این دسته حذف شود؟')) return
      this.$store.dispatch('Category/removeCategoryImage', this.category)
      this.form.preview = ''
      this.form.image = []
      this.$emit('image-removed', this.category)
    },
    isSelectedParent(node) {
      return categoryKey(this.form.parent_id) === node.key
    },
    selectParent(node) {
      if (this.isSelectedParent(node)) {
        this.clearParent()
        return
      }
      this.form.parent_id = node.data.id
      this.form.parent = node.data
    },
    clearParent() {
      this.form.parent_id = null
      this.form.parent = {}
    },
    async onSubmit() {
      if (this.saving) return
      this.$v.$touch()
      if (this.$v.$invalid) {
        toast('خطا', 'لطفاً فیلدهای الزامی را کامل کنید.', 'danger')
        return
      }
      this.saving = true
      const payload = {...this.form, description: this.form.description || ''}
      try {
        const action = this.isEdit ? 'Category/updateCategory' : 'Category/createCategory'
        const res = await this.$store.dispatch(action, payload)
        const saved = (res && res.data && res.data.data) || {}
        const result = {
          ...(this.isEdit ? this.category : {}),
          ...saved,
          id: saved.id || this.form.id,
          name: saved.name || this.form.name,
          slug: saved.slug || this.form.slug,
          parent_id: saved.parent_id !== undefined ? saved.parent_id : this.form.parent_id,
          language_id: saved.language_id !== undefined ? saved.language_id : this.form.language_id,
          order: saved.order !== undefined ? saved.order : this.form.order,
          status: saved.status !== undefined ? saved.status : (this.form.status ? 1 : 0),
          description: saved.description !== undefined ? saved.description : this.form.description,
          image: saved.image !== undefined ? saved.image : (this.form.preview || null),
        }
        delete result.children
        if (this.isEdit) {
          toast('عملیات با موفقیت انجام شد', 'دسته بندی با موفقیت ویرایش شد.', 'success')
        }
        this.$emit('saved', result, {created: !this.isEdit})
        if (!this.isEdit) this.fillForm()
      } catch (e) {
        console.log(e)
        toast('خطا', 'ذخیره دسته انجام نشد.', 'danger')
      } finally {
        this.saving = false
      }
    },
  },
  created() {
    this.fillForm()
  },
}
</script>

<style scoped>
.cat-form {
  --dash-primary: #eb3d60;
  --dash-primary-soft: rgba(235, 61, 96, 0.1);
  --line: rgba(30, 30, 30, 0.08);
  --ink: #1e1e1e;
  --ink-soft: #424242;
  display: grid;
  grid-template-columns: minmax(0, 1fr) 300px;
  gap: 1rem;
  align-items: start;
}

.cat-side {
  display: grid;
  gap: 1rem;
}

.cat-form--page .cat-side {
  position: sticky;
  top: 0.75rem;
}

.cat-card {
  border: 1px solid var(--line);
  border-radius: 18px;
  background: #fff;
  box-shadow: 0 10px 28px rgba(30, 30, 30, 0.05);
  overflow: hidden;
}

.cat-card__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  padding: 0.8rem 1rem;
  border-bottom: 1px solid var(--line);
  background: linear-gradient(120deg, rgba(235, 61, 96, 0.08), transparent 46%), #fff;
}

.cat-card__title {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin: 0;
  font-size: 0.98rem;
  font-weight: 800;
  color: var(--ink);
}

.cat-card__icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.7rem;
  height: 1.7rem;
  border-radius: 10px;
  background: rgba(235, 61, 96, 0.12);
  color: var(--dash-primary);
  font-size: 0.95rem;
}

.cat-card__body {
  padding: 1rem;
}

.cat-stack > * + * {
  margin-top: 1rem;
}

.cat-card__body >>> .form-group {
  margin-bottom: 0;
}

.cat-grid {
  display: grid;
  grid-template-columns: 1fr 1fr 120px;
  gap: 0.85rem;
}

.cat-label {
  display: block;
  margin-bottom: 0.4rem;
  font-weight: 700;
  font-size: 0.84rem;
  color: var(--ink-soft);
}

.cat-input {
  border-radius: 12px !important;
  border-color: var(--line);
}

.cat-input:focus {
  border-color: var(--dash-primary);
  box-shadow: 0 0 0 3px rgba(235, 61, 96, 0.12);
}

.cat-muted {
  color: #8a8a96;
  font-size: 0.8rem;
}

.cat-parent__current {
  margin-bottom: 0.5rem;
}

.cat-chip {
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  padding: 0.25rem 0.35rem 0.25rem 0.7rem;
  border-radius: 999px;
  background: var(--dash-primary-soft);
  color: var(--dash-primary);
  font-weight: 700;
  font-size: 0.8rem;
}

.cat-chip__path {
  opacity: 0.7;
  font-weight: 600;
}

.cat-chip__x {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  padding: 0;
  border: 0;
  border-radius: 50%;
  background: rgba(235, 61, 96, 0.15);
  color: var(--dash-primary);
}

.cat-search {
  position: relative;
  margin-bottom: 0.5rem;
}

.cat-search__icon {
  position: absolute;
  top: 50%;
  right: 0.7rem;
  transform: translateY(-50%);
  color: #9a9aa5;
}

.cat-search__input {
  width: 100%;
  height: 36px;
  padding: 0 2.1rem 0 0.8rem;
  border: 1px solid var(--line);
  border-radius: 11px;
  font-size: 0.82rem;
  outline: none;
}

.cat-search__input:focus {
  border-color: var(--dash-primary);
  box-shadow: 0 0 0 3px rgba(235, 61, 96, 0.12);
}

.cat-picker {
  max-height: 220px;
  overflow-y: auto;
  padding: 0.3rem;
  border: 1px solid var(--line);
  border-radius: 12px;
  background: #fcfcfd;
}

.cat-picker__item {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  width: 100%;
  padding: 0.4rem 0.6rem;
  border: 0;
  border-radius: 9px;
  background: transparent;
  color: var(--ink-soft);
  font-size: 0.83rem;
  text-align: right;
  cursor: pointer;
}

.cat-picker__item:hover {
  background: rgba(235, 61, 96, 0.06);
}

.cat-picker__item.is-active {
  background: var(--dash-primary-soft);
  color: var(--dash-primary);
  font-weight: 700;
}

.cat-picker__item.is-match .cat-picker__name {
  text-decoration: underline;
  text-decoration-color: rgba(235, 61, 96, 0.4);
}

.cat-picker__icon {
  color: #b0b0b8;
  flex: 0 0 auto;
}

.cat-picker__name {
  flex: 1 1 auto;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.cat-picker__empty {
  margin: 0;
  padding: 0.8rem;
  text-align: center;
  color: #9a9aa5;
  font-size: 0.8rem;
}

.cat-switch-row {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  margin: 0;
  white-space: nowrap;
}

.cat-switch-row__text {
  font-size: 0.78rem;
  font-weight: 700;
  color: #8a8a96;
}

.cat-switch-row.is-on .cat-switch-row__text {
  color: var(--dash-primary);
}

.cat-switch {
  position: relative;
  width: 42px;
  height: 24px;
  padding: 0;
  border: 0;
  border-radius: 999px;
  background: #d4d4da;
  cursor: pointer;
  transition: background 0.2s ease;
}

.cat-switch.is-on {
  background: var(--dash-primary);
}

.cat-switch__knob {
  position: absolute;
  top: 3px;
  right: 3px;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: #fff;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
  transition: transform 0.2s ease;
}

.cat-switch.is-on .cat-switch__knob {
  transform: translateX(-18px);
}

.cat-image-picker {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.4rem;
  width: 100%;
  aspect-ratio: 1 / 1;
  max-height: 240px;
  margin: 0;
  border-radius: 16px;
  overflow: hidden;
  cursor: pointer;
  background: #f7f7f8;
}

.cat-image-picker.is-empty {
  border: 1.5px dashed rgba(30, 30, 30, 0.18);
  color: #6b6b6b;
  font-weight: 700;
  font-size: 0.82rem;
}

.cat-image-picker.is-empty:hover {
  border-color: var(--dash-primary);
  color: var(--dash-primary);
}

.cat-image-picker__icon {
  font-size: 1.9rem;
}

.cat-image-picker img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.cat-image-picker__change {
  position: absolute;
  bottom: 0.5rem;
  right: 50%;
  transform: translateX(50%);
  padding: 0.25rem 0.7rem;
  border-radius: 999px;
  background: rgba(30, 30, 30, 0.7);
  color: #fff;
  font-size: 0.74rem;
  font-weight: 700;
  white-space: nowrap;
}

.cat-link-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.2rem 0.55rem;
  border: 0;
  border-radius: 999px;
  background: transparent;
  font-size: 0.76rem;
  font-weight: 700;
}

.cat-link-btn.is-danger {
  color: #dc3545;
}

.cat-link-btn.is-danger:hover {
  background: rgba(220, 53, 69, 0.08);
}

.cat-status-pill {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.2rem 0.6rem;
  border-radius: 999px;
  background: #f3f3f5;
  color: #8a8a96;
  font-size: 0.74rem;
  font-weight: 700;
  white-space: nowrap;
}

.cat-status-pill.is-set {
  background: rgba(25, 135, 84, 0.1);
  color: #198754;
}

.cat-status-pill__dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: currentColor;
}

.cat-tabs {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  padding: 4px;
  border-radius: 12px;
  background: #f3f3f5;
  border: 1px solid var(--line);
}

.cat-tab {
  flex: 1 1 auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.3rem;
  padding: 0.42rem 0.7rem;
  border: 0;
  border-radius: 9px;
  background: transparent;
  color: var(--ink-soft);
  font-size: 0.82rem;
  font-weight: 700;
}

.cat-tab.is-active {
  background: #fff;
  color: var(--dash-primary);
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.08);
}

.cat-hint {
  margin: 0.85rem 0 0.4rem;
  color: #8a8a96;
  font-size: 0.76rem;
  font-weight: 700;
}

.cat-translations {
  display: flex;
  flex-wrap: wrap;
  gap: 0.35rem;
}

.cat-add-translation {
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  padding: 0.3rem 0.7rem;
  border: 1px dashed rgba(235, 61, 96, 0.4);
  border-radius: 999px;
  background: transparent;
  color: var(--dash-primary);
  font-size: 0.76rem;
  font-weight: 700;
}

.cat-add-translation:hover {
  background: var(--dash-primary-soft);
}

.cat-actions {
  display: flex;
  gap: 0.5rem;
}

.cat-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.4rem;
  min-height: 46px;
  padding: 0 1.2rem;
  border-radius: 999px;
  font-weight: 800;
  border: 0;
  cursor: pointer;
}

.cat-btn--primary {
  flex: 1 1 auto;
  background: var(--dash-primary);
  color: #fff;
  box-shadow: 0 10px 24px rgba(235, 61, 96, 0.3);
}

.cat-btn--primary:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.cat-btn--ghost {
  background: #f3f3f5;
  color: var(--ink-soft);
}

@media (max-width: 991.98px) {
  .cat-form {
    grid-template-columns: 1fr;
    grid-template-areas:
      "image"
      "main"
      "lang"
      "actions";
  }

  /* mobile: side cards join the main grid so the image comes first */
  .cat-side,
  .cat-form--page .cat-side {
    display: contents;
  }

  .cat-area-main { grid-area: main; }
  .cat-area-image { grid-area: image; }
  .cat-area-lang { grid-area: lang; }
  .cat-area-actions { grid-area: actions; }

  .cat-image-picker {
    max-width: 200px;
    margin: 0 auto;
  }
}

@media (max-width: 575.98px) {
  .cat-grid {
    grid-template-columns: 1fr 1fr;
  }

  .cat-grid > :first-child {
    grid-column: 1 / -1;
  }
}
</style>
